import React, { useRef, useState } from 'react';
import { ToneAnalysis, ToneScore } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface ToneSummaryCardProps {
  toneAnalysis?: ToneAnalysis;
  onViewDetails: () => void;
}

interface ToneBarProps {
  label: string;
  toneScore: ToneScore;
  isPositive: boolean;
}

const ToneBar: React.FC<ToneBarProps> = ({ label, toneScore, isPositive }) => {
  const fillColor = isPositive
    ? toneScore.score >= 60 ? 'bg-green-500' : toneScore.score >= 30 ? 'bg-green-300' : 'bg-gray-200'
    : toneScore.score >= 60 ? 'bg-red-500' : toneScore.score >= 30 ? 'bg-red-300' : 'bg-gray-200';

  const textColor = isPositive ? 'text-green-700' : 'text-red-700';

  const barRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const handleMouseEnter = () => {
    if (!barRef.current || !toneScore.explanation) return;
    const rect = barRef.current.getBoundingClientRect();
    setTooltipPos({ x: rect.left, y: rect.top });
  };

  const handleMouseLeave = () => setTooltipPos(null);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        <span className={`text-xs font-bold ${textColor}`}>{toneScore.score}%</span>
      </div>
      <div
        ref={barRef}
        className="h-2 bg-gray-100 rounded-full overflow-hidden cursor-help"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`h-full rounded-full transition-all duration-500 ${fillColor}`}
          style={{ width: `${toneScore.score}%` }}
        />
      </div>
      {tooltipPos && toneScore.explanation && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPos.x,
            top: tooltipPos.y - 8,
            transform: 'translateY(-100%)',
            zIndex: 9999,
            maxWidth: 260,
          }}
          className="bg-gray-800 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl pointer-events-none"
        >
          <p className="leading-relaxed">{toneScore.explanation}</p>
          <p className="mt-1 text-gray-400">AI confidence: {toneScore.confidence_level}%</p>
        </div>
      )}
    </div>
  );
};

const ToneSummaryCard: React.FC<ToneSummaryCardProps> = ({ toneAnalysis, onViewDetails }) => {
  if (!toneAnalysis?.positive) {
    return (
      <SummarySectionCard
        title="Tone (Sales Rep)"
        icon={<span>&#127908;</span>}
        resultContent={null}
        missingDataMessage="Tone data not available. Re-analyze this session to generate tone analysis."
        onViewDetails={onViewDetails}
      />
    );
  }

  const { positive, negative, overall_coaching } = toneAnalysis;

  const highScore = Math.max(
    positive.confidence.score,
    positive.optimism.score
  );
  const lowConcern = Math.max(
    negative.defensiveness.score,
    negative.confrontation.score
  );

  const badgeColor = lowConcern >= 50
    ? 'bg-red-100 text-red-700 border border-red-200'
    : highScore >= 60
    ? 'bg-green-100 text-green-700 border border-green-200'
    : 'bg-yellow-100 text-yellow-700 border border-yellow-200';

  const badgeLabel = lowConcern >= 50 ? 'Needs Attention' : highScore >= 60 ? 'Strong Tone' : 'Moderate';

  const statusBadge = (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${badgeColor}`}>
      {badgeLabel}
    </span>
  );

  const resultContent = (
    <div className="space-y-3">
      <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wider">Positive</p>
      <div className="grid grid-cols-1 gap-3">
        <ToneBar label="Confidence" toneScore={positive.confidence} isPositive={true} />
        <ToneBar label="Optimism" toneScore={positive.optimism} isPositive={true} />
      </div>
      <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider pt-1">Negative</p>
      <div className="grid grid-cols-1 gap-3">
        <ToneBar label="Defensiveness" toneScore={negative.defensiveness} isPositive={false} />
        <ToneBar label="Confrontation" toneScore={negative.confrontation} isPositive={false} />
      </div>
    </div>
  );

  return (
    <SummarySectionCard
      title="Tone (Sales Rep)"
      icon={<span>&#127908;</span>}
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={overall_coaching ? [overall_coaching] : undefined}
      sectionKey="tone"
      onViewDetails={onViewDetails}
    />
  );
};

export default ToneSummaryCard;
